import moment from 'moment';
import { useCookies } from 'react-cookie';
import { isPartner } from 'src/shared/affiliateTracking';
import { useSelector } from '../redux';
import { MAM_COOKIE_NAME } from 'src/shared/constants/cookies';
import { ASSETS_URL } from 'src/shared/urls';

export const usePromotions = () => {
  const { partnerIDs, promotions, splash } = useSelector((state) => state.app.config);
  const { user, loaded: userLoaded } = useSelector((state) => state.auth);
  const [cookies] = useCookies([MAM_COOKIE_NAME]);

  const startDate = moment(promotions?.promoCodeStart);
  const endDate = moment(promotions?.promoCodeEnd);
  const isCurrentTimeBetweenStartAndEndDates = moment().isBetween(startDate, endDate, undefined, '[]');

  const popupDisabledPaths = [
    '/',
    '',
    ...(splash?.active ? [] : ['/updates', '/updates/archive', '/updates/upcoming']),
  ];

  // Promo is considered active if:
  //   1. Promotions are enabled on the current site through the database
  //   2. Current time falls between the start and end dates of the promo
  //   3. User has no MAM cookie or their MAM code is not blacklisted for promos
  const isPromoActive =
    !!promotions?.enabled && isCurrentTimeBetweenStartAndEndDates && !isPartner(cookies[MAM_COOKIE_NAME], partnerIDs);

  const isUserWithSubscription = !!user?.validSubscription;

  // Specific promo sections are active if
  //   1. Promo is considered enabled (all of the above is true)
  //   2. Guest user or user WITHOUT a valid subscription on the current site
  //   3. Their respective section option is set to true in the promotions object in the config database
  //   4. (Optional) Check if the user data has been loaded first to not flicker banners on the page for active users
  const isPromoBannerActive = isPromoActive && !!promotions?.bannerEnabled && !isUserWithSubscription && userLoaded;

  const isPromoInlineBannerActive =
    isPromoActive && !!promotions?.inlineBannerEnabled && !isUserWithSubscription && userLoaded;

  const isPromoFooterActive = isPromoActive && !!promotions?.footerEnabled && !isUserWithSubscription;

  const isPromoPopupActive = isPromoActive && !!promotions?.popupEnabled && !isUserWithSubscription;

  const isPromoRibbonActive = isPromoActive && !!promotions?.ribbonEnabled && !isUserWithSubscription && userLoaded;

  const isPromoSplashFooterActive =
    isPromoActive && !!promotions?.splashFooterEnabled && !isUserWithSubscription && userLoaded;

  // Promo gateway is active if
  //   1. Promo is considered enabled (all of the above is true)
  //   2. User WITH a valid subscription on the current site
  //   3. `gatewayEnabled` is set to true on the promotions object in the config database
  //   Note: if promo gateway is not enabled, the default gateway will be shown
  const isPromoGatewayActive = isPromoActive && !!promotions?.gatewayEnabled && isUserWithSubscription;

  const isPromoGatewayLightBackground = isPromoGatewayActive && promotions?.gatewayBackgroundColor === 'light';

  const getPromoImage = (pathParts: string[]) => {
    const constructedPath = ['movies', 'offer', 'promos', promotions?.cdnFolder, ...pathParts]
      .join('/')
      .replace(/\/+/g, '/');

    return new URL(constructedPath, ASSETS_URL).toString();
  };

  return {
    popupDisabledPaths, // this default will be overriden if promotions config has it set on the next line
    ...promotions,
    getPromoImage,
    isPromoActive,
    isPromoBannerActive,
    isPromoFooterActive,
    isPromoGatewayActive,
    isPromoGatewayLightBackground,
    isPromoInlineBannerActive,
    isPromoPopupActive,
    isPromoRibbonActive,
    isPromoSplashFooterActive,
  };
};
